/**
 * Document Templates Management Page
 * Phase D1: Template-Based Document Issuance System
 * 
 * Features:
 * - Template List with category grouping and filters
 * - Template Editor with placeholder helper
 * - Manual fields schema editor
 * - Publish/Archive actions
 * - Preview with mock data or real employee
 * 
 * Access: HR_MANAGER + SUPER_ADMIN (edit), HR roles (view only)
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  FileText, 
  Search, 
  Plus, 
  Edit2, 
  Eye, 
  Archive, 
  CheckCircle2, 
  AlertTriangle,
  Save,
  Code,
  Info,
  Copy,
  X,
  FolderOpen,
  FileCheck,
  Filter
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { sanitizeHtml } from '@/lib/sanitize';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Status badge colors
const STATUS_COLORS = {
  draft: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Draft', labelAr: 'مسودة' },
  published: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Published', labelAr: 'منشور' },
  archived: { bg: 'bg-slate-100', text: 'text-slate-500', label: 'Archived', labelAr: 'مؤرشف' },
};

// Manual field types
const FIELD_TYPES = [
  { value: 'text', label: 'Text', labelAr: 'نص' },
  { value: 'textarea', label: 'Long Text', labelAr: 'نص طويل' },
  { value: 'number', label: 'Number', labelAr: 'رقم' },
  { value: 'date', label: 'Date', labelAr: 'تاريخ' },
  { value: 'currency', label: 'Currency (SAR)', labelAr: 'مبلغ (ريال)' },
  { value: 'select', label: 'Dropdown', labelAr: 'قائمة منسدلة' },
];

export default function DocumentTemplatesPage() {
  const { token, user } = useAuth();
  const { isRTL } = useLanguage();
  
  // Check edit permission
  const canEdit = user?.role === 'super_admin' || user?.role === 'hr_manager';
  
  // State
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [docTypeFilter, setDocTypeFilter] = useState('all');
  
  // Editor state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLanguage, setPreviewLanguage] = useState('ar');
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    content_ar: '',
    content_en: '',
    manual_fields: [],
    change_notes: ''
  });
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showPlaceholdersDialog, setShowPlaceholdersDialog] = useState(false);
  
  // New template form
  const [newTemplateForm, setNewTemplateForm] = useState({
    document_type_code: '',
    content_ar: '',
    content_en: '',
    manual_fields: [],
    change_notes: ''
  });
  
  // Saving state
  const [saving, setSaving] = useState(false);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      const [templatesRes, typesRes, placeholdersRes] = await Promise.all([
        axios.get(`${API_URL}/document-templates`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/document-templates/types?supports_template=true`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/document-templates/placeholders`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { auto_placeholders: [] } }))
      ]);
      
      setTemplates(templatesRes.data.templates || []);
      setDocumentTypes(typesRes.data.document_types || []);
      setCategories(typesRes.data.categories || []);
      setPlaceholders(placeholdersRes.data?.auto_placeholders || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(isRTL ? 'فشل تحميل البيانات' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Filter templates
  const filteredTemplates = useMemo(() => {
    return templates.filter(t => {
      // Search
      if (searchQuery) {
        const search = searchQuery.toLowerCase();
        const matchesCode = t.document_type_code?.toLowerCase().includes(search);
        const matchesNameEn = t.document_type_name_en?.toLowerCase().includes(search);
        const matchesNameAr = t.document_type_name_ar?.includes(search);
        if (!matchesCode && !matchesNameEn && !matchesNameAr) return false;
      }
      // Status
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      // Category
      if (categoryFilter !== 'all' && t.category !== categoryFilter) return false;
      // Doc type
      if (docTypeFilter !== 'all' && t.document_type_code !== docTypeFilter) return false;
      return true;
    });
  }, [templates, searchQuery, statusFilter, categoryFilter, docTypeFilter]);

  // Get templates grouped by category
  const groupedTemplates = useMemo(() => {
    const grouped = {};
    filteredTemplates.forEach(t => {
      const cat = t.category || 'other';
      if (!grouped[cat]) {
        const catInfo = categories.find(c => c.code === cat);
        grouped[cat] = {
          name_en: catInfo?.name_en || cat,
          name_ar: catInfo?.name_ar || cat,
          templates: []
        };
      }
      grouped[cat].templates.push(t);
    });
    return grouped;
  }, [filteredTemplates, categories]);

  // Handle template selection for editing
  const handleSelectTemplate = async (template) => {
    setSelectedTemplate(template);
    setEditForm({
      content_ar: template.content_ar || '',
      content_en: template.content_en || '',
      manual_fields: template.manual_fields || [],
      change_notes: ''
    });
    setEditMode(template.status === 'draft');
  };

  // Create new template
  const handleCreateTemplate = async () => {
    if (!newTemplateForm.document_type_code) {
      toast.error(isRTL ? 'يرجى اختيار نوع الوثيقة' : 'Please select a document type');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/document-templates`, newTemplateForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(isRTL ? 'تم إنشاء القالب بنجاح' : 'Template created successfully');
      setShowCreateDialog(false);
      setNewTemplateForm({
        document_type_code: '',
        content_ar: '',
        content_en: '',
        manual_fields: [],
        change_notes: ''
      });
      fetchData();
      handleSelectTemplate(response.data);
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error(error.response?.data?.detail?.error || 'Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  // Update template
  const handleUpdateTemplate = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      await axios.put(`${API_URL}/document-templates/${selectedTemplate.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(isRTL ? 'تم حفظ التغييرات' : 'Changes saved');
      fetchData();
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error(error.response?.data?.detail?.error || 'Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  // Publish template
  const handlePublishTemplate = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/document-templates/${selectedTemplate.id}/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(isRTL ? 'تم نشر القالب بنجاح' : 'Template published successfully');
      setShowPublishDialog(false);
      fetchData();
      
      // Update selected template
      if (response.data.template) {
        setSelectedTemplate(response.data.template);
        setEditMode(false);
      }
    } catch (error) {
      console.error('Error publishing template:', error);
      toast.error(error.response?.data?.detail?.error || 'Failed to publish template');
    } finally {
      setSaving(false);
    }
  };

  // Archive template
  const handleArchiveTemplate = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      await axios.post(`${API_URL}/document-templates/${selectedTemplate.id}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(isRTL ? 'تم أرشفة القالب' : 'Template archived');
      setShowArchiveDialog(false);
      fetchData();
      setSelectedTemplate(null);
    } catch (error) {
      console.error('Error archiving template:', error);
      toast.error(error.response?.data?.detail?.error || 'Failed to archive template');
    } finally {
      setSaving(false);
    }
  };

  // Preview template
  const handlePreviewTemplate = async () => {
    if (!selectedTemplate) return;
    
    try {
      const response = await axios.get(
        `${API_URL}/document-templates/${selectedTemplate.id}/preview?language=${previewLanguage}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPreviewData(response.data);
      setShowPreviewDialog(true);
    } catch (error) {
      console.error('Error previewing template:', error);
      toast.error(isRTL ? 'فشل تحميل المعاينة' : 'Failed to load preview');
    }
  };

  // Add manual field
  const addManualField = () => {
    const newField = {
      key: `field_${Date.now()}`,
      label_en: '',
      label_ar: '',
      type: 'text',
      required: false,
      options: []
    };
    setEditForm(prev => ({
      ...prev,
      manual_fields: [...prev.manual_fields, newField]
    }));
  };

  // Remove manual field
  const removeManualField = (index) => {
    setEditForm(prev => ({
      ...prev,
      manual_fields: prev.manual_fields.filter((_, i) => i !== index)
    }));
  };

  // Update manual field
  const updateManualField = (index, updates) => {
    setEditForm(prev => ({
      ...prev,
      manual_fields: prev.manual_fields.map((field, i) => 
        i === index ? { ...field, ...updates } : field
      )
    }));
  };

  // Copy placeholder to clipboard
  const copyPlaceholder = (key) => {
    navigator.clipboard.writeText(`{{${key}}}`);
    toast.success(isRTL ? 'تم النسخ' : 'Copied!');
  };

  // Document types for dropdown (grouped by category)
  const documentTypesForDropdown = useMemo(() => {
    // Only show template-supported types without existing templates
    const existingCodes = templates.map(t => t.document_type_code);
    return documentTypes.filter(dt => !existingCodes.includes(dt.code));
  }, [documentTypes, templates]);

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="document-templates-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isRTL ? 'قوالب الوثائق' : 'Document Templates'}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {isRTL 
              ? 'إدارة قوالب وثائق الموظفين للإصدار' 
              : 'Manage employee document templates for issuance'}
          </p>
        </div>
        
        {canEdit && (
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="gap-2"
            data-testid="create-template-btn"
          >
            <Plus className="h-4 w-4" />
            {isRTL ? 'إنشاء قالب' : 'Create Template'}
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={isRTL ? 'بحث...' : 'Search...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="template-search-input"
              />
            </div>
            
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]" data-testid="category-filter">
                <FolderOpen className="h-4 w-4 mr-2" />
                <SelectValue placeholder={isRTL ? 'الفئة' : 'Category'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isRTL ? 'كل الفئات' : 'All Categories'}</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.code} value={cat.code}>
                    {isRTL ? cat.name_ar : cat.name_en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]" data-testid="status-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder={isRTL ? 'الحالة' : 'Status'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isRTL ? 'كل الحالات' : 'All Statuses'}</SelectItem>
                <SelectItem value="draft">{isRTL ? 'مسودة' : 'Draft'}</SelectItem>
                <SelectItem value="published">{isRTL ? 'منشور' : 'Published'}</SelectItem>
                <SelectItem value="archived">{isRTL ? 'مؤرشف' : 'Archived'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Main Content: Templates List and Editor */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Templates List */}
        <div className="lg:col-span-1">
          <Card className="h-[600px] flex flex-col">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">
                {isRTL ? 'القوالب' : 'Templates'}
                <Badge variant="secondary" className="ml-2">
                  {filteredTemplates.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden p-0">
              <ScrollArea className="h-full px-4 pb-4">
                {Object.entries(groupedTemplates).length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>{isRTL ? 'لا توجد قوالب' : 'No templates found'}</p>
                  </div>
                ) : (
                  Object.entries(groupedTemplates).map(([catCode, catData]) => (
                    <div key={catCode} className="mb-4">
                      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                        {isRTL ? catData.name_ar : catData.name_en}
                      </div>
                      <div className="space-y-2">
                        {catData.templates.map(template => (
                          <button
                            key={template.id}
                            onClick={() => handleSelectTemplate(template)}
                            className={cn(
                              "w-full text-left p-3 rounded-lg border transition-colors",
                              selectedTemplate?.id === template.id
                                ? "border-blue-500 bg-blue-50"
                                : "border-slate-200 hover:bg-slate-50"
                            )}
                            data-testid={`template-item-${template.document_type_code}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-sm truncate">
                                  {isRTL ? template.document_type_name_ar : template.document_type_name_en}
                                </p>
                                <p className="text-xs text-slate-500 truncate">
                                  v{template.version} • {template.document_type_code}
                                </p>
                              </div>
                              <Badge className={cn(
                                "text-xs shrink-0",
                                STATUS_COLORS[template.status]?.bg,
                                STATUS_COLORS[template.status]?.text
                              )}>
                                {isRTL 
                                  ? STATUS_COLORS[template.status]?.labelAr 
                                  : STATUS_COLORS[template.status]?.label}
                              </Badge>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Template Editor */}
        <div className="lg:col-span-2">
          {selectedTemplate ? (
            <Card className="h-[600px] flex flex-col">
              <CardHeader className="pb-2 shrink-0">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">
                      {isRTL 
                        ? selectedTemplate.document_type_name_ar 
                        : selectedTemplate.document_type_name_en}
                    </CardTitle>
                    <CardDescription>
                      v{selectedTemplate.version} • {selectedTemplate.document_type_code}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={cn(
                      STATUS_COLORS[selectedTemplate.status]?.bg,
                      STATUS_COLORS[selectedTemplate.status]?.text
                    )}>
                      {isRTL 
                        ? STATUS_COLORS[selectedTemplate.status]?.labelAr 
                        : STATUS_COLORS[selectedTemplate.status]?.label}
                    </Badge>
                    {selectedTemplate.status === 'draft' && canEdit && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowPublishDialog(true)}
                        className="gap-1"
                        data-testid="publish-template-btn"
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        {isRTL ? 'نشر' : 'Publish'}
                      </Button>
                    )}
                    {selectedTemplate.status !== 'archived' && canEdit && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowArchiveDialog(true)}
                        className="text-slate-500"
                        data-testid="archive-template-btn"
                      >
                        <Archive className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden p-0">
                <Tabs defaultValue="content" className="h-full flex flex-col">
                  <TabsList className="mx-4 mt-2 shrink-0">
                    <TabsTrigger value="content">
                      {isRTL ? 'المحتوى' : 'Content'}
                    </TabsTrigger>
                    <TabsTrigger value="fields">
                      {isRTL ? 'الحقول' : 'Fields'}
                    </TabsTrigger>
                    <TabsTrigger value="preview">
                      {isRTL ? 'معاينة' : 'Preview'}
                    </TabsTrigger>
                  </TabsList>
                  
                  {/* Content Tab */}
                  <TabsContent value="content" className="flex-1 overflow-auto px-4 pb-4 mt-0">
                    <div className="space-y-4 pt-4">
                      {/* Placeholder Helper Button */}
                      <div className="flex justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowPlaceholdersDialog(true)}
                          className="gap-1"
                        >
                          <Code className="h-4 w-4" />
                          {isRTL ? 'المتغيرات' : 'Placeholders'}
                        </Button>
                      </div>
                      
                      {/* Arabic Content */}
                      <div>
                        <Label>{isRTL ? 'المحتوى العربي' : 'Arabic Content'}</Label>
                        <Textarea
                          value={editForm.content_ar}
                          onChange={(e) => setEditForm(prev => ({
                            ...prev,
                            content_ar: e.target.value
                          }))}
                          disabled={!editMode}
                          className="min-h-[150px] font-arabic text-right"
                          dir="rtl"
                          placeholder={isRTL ? 'أدخل المحتوى العربي...' : 'Enter Arabic content...'}
                          data-testid="content-ar-textarea"
                        />
                      </div>
                      
                      {/* English Content */}
                      <div>
                        <Label>{isRTL ? 'المحتوى الإنجليزي' : 'English Content'}</Label>
                        <Textarea
                          value={editForm.content_en}
                          onChange={(e) => setEditForm(prev => ({
                            ...prev,
                            content_en: e.target.value
                          }))}
                          disabled={!editMode}
                          className="min-h-[150px]"
                          placeholder={isRTL ? 'أدخل المحتوى الإنجليزي...' : 'Enter English content...'}
                          data-testid="content-en-textarea"
                        />
                      </div>
                      
                      {/* Change Notes */}
                      {editMode && (
                        <div>
                          <Label>{isRTL ? 'ملاحظات التغيير' : 'Change Notes'}</Label>
                          <Input
                            value={editForm.change_notes}
                            onChange={(e) => setEditForm(prev => ({
                              ...prev,
                              change_notes: e.target.value
                            }))}
                            placeholder={isRTL ? 'ما الذي تغير؟' : 'What changed?'}
                          />
                        </div>
                      )}
                      
                      {/* Save Button */}
                      {editMode && canEdit && (
                        <Button
                          onClick={handleUpdateTemplate}
                          disabled={saving}
                          className="w-full gap-2"
                          data-testid="save-template-btn"
                        >
                          <Save className="h-4 w-4" />
                          {saving 
                            ? (isRTL ? 'جاري الحفظ...' : 'Saving...') 
                            : (isRTL ? 'حفظ التغييرات' : 'Save Changes')}
                        </Button>
                      )}
                    </div>
                  </TabsContent>
                  
                  {/* Fields Tab */}
                  <TabsContent value="fields" className="flex-1 overflow-auto px-4 pb-4 mt-0">
                    <div className="space-y-4 pt-4">
                      <div className="flex items-center justify-between">
                        <Label className="text-base">
                          {isRTL ? 'الحقول اليدوية' : 'Manual Fields'}
                        </Label>
                        {editMode && canEdit && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={addManualField}
                            className="gap-1"
                            data-testid="add-field-btn"
                          >
                            <Plus className="h-4 w-4" />
                            {isRTL ? 'إضافة حقل' : 'Add Field'}
                          </Button>
                        )}
                      </div>
                      
                      {editForm.manual_fields.length === 0 ? (
                        <div className="text-center py-8 text-slate-500 border-2 border-dashed rounded-lg">
                          <Info className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p>{isRTL ? 'لا توجد حقول يدوية' : 'No manual fields defined'}</p>
                          <p className="text-xs mt-1">
                            {isRTL 
                              ? 'أضف حقولاً ليملأها المستخدم عند الإصدار' 
                              : 'Add fields for users to fill when issuing'}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {editForm.manual_fields.map((field, index) => (
                            <Card key={index} className="p-3">
                              <div className="grid gap-3 md:grid-cols-4">
                                <div>
                                  <Label className="text-xs">{isRTL ? 'المفتاح' : 'Key'}</Label>
                                  <Input
                                    value={field.key}
                                    onChange={(e) => updateManualField(index, { 
                                      key: e.target.value.replace(/\s/g, '_').toLowerCase() 
                                    })}
                                    disabled={!editMode}
                                    className="text-sm"
                                    placeholder="field_key"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">{isRTL ? 'العنوان (EN)' : 'Label (EN)'}</Label>
                                  <Input
                                    value={field.label_en}
                                    onChange={(e) => updateManualField(index, { label_en: e.target.value })}
                                    disabled={!editMode}
                                    className="text-sm"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">{isRTL ? 'العنوان (AR)' : 'Label (AR)'}</Label>
                                  <Input
                                    value={field.label_ar}
                                    onChange={(e) => updateManualField(index, { label_ar: e.target.value })}
                                    disabled={!editMode}
                                    className="text-sm"
                                    dir="rtl"
                                  />
                                </div>
                                <div className="flex items-end gap-2">
                                  <div className="flex-1">
                                    <Label className="text-xs">{isRTL ? 'النوع' : 'Type'}</Label>
                                    <Select
                                      value={field.type}
                                      onValueChange={(val) => updateManualField(index, { type: val })}
                                      disabled={!editMode}
                                    >
                                      <SelectTrigger className="text-sm">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {FIELD_TYPES.map(ft => (
                                          <SelectItem key={ft.value} value={ft.value}>
                                            {isRTL ? ft.labelAr : ft.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  {editMode && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => removeManualField(index)}
                                      className="text-red-500 hover:text-red-700"
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                              <div className="mt-2 flex items-center gap-2">
                                <Checkbox
                                  id={`required-${index}`}
                                  checked={field.required}
                                  onCheckedChange={(checked) => updateManualField(index, { required: checked })}
                                  disabled={!editMode}
                                />
                                <Label htmlFor={`required-${index}`} className="text-xs">
                                  {isRTL ? 'إلزامي' : 'Required'}
                                </Label>
                              </div>
                            </Card>
                          ))}
                        </div>
                      )}
                      
                      {/* Save Button */}
                      {editMode && canEdit && editForm.manual_fields.length > 0 && (
                        <Button
                          onClick={handleUpdateTemplate}
                          disabled={saving}
                          className="w-full gap-2"
                        >
                          <Save className="h-4 w-4" />
                          {saving 
                            ? (isRTL ? 'جاري الحفظ...' : 'Saving...') 
                            : (isRTL ? 'حفظ التغييرات' : 'Save Changes')}
                        </Button>
                      )}
                    </div>
                  </TabsContent>
                  
                  {/* Preview Tab */}
                  <TabsContent value="preview" className="flex-1 overflow-auto px-4 pb-4 mt-0">
                    <div className="space-y-4 pt-4">
                      <div className="flex items-center gap-4">
                        <Label>{isRTL ? 'لغة المعاينة' : 'Preview Language'}</Label>
                        <Select value={previewLanguage} onValueChange={setPreviewLanguage}>
                          <SelectTrigger className="w-[150px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ar">{isRTL ? 'العربية' : 'Arabic'}</SelectItem>
                            <SelectItem value="en">{isRTL ? 'الإنجليزية' : 'English'}</SelectItem>
                            <SelectItem value="ar_en">{isRTL ? 'ثنائي اللغة' : 'Bilingual'}</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        <Button
                          onClick={handlePreviewTemplate}
                          variant="outline"
                          className="gap-1"
                          data-testid="preview-template-btn"
                        >
                          <Eye className="h-4 w-4" />
                          {isRTL ? 'معاينة' : 'Preview'}
                        </Button>
                      </div>
                      
                      {previewData ? (
                        <div className="border rounded-lg p-4 bg-white">
                          <div className="prose max-w-none" dir={previewLanguage === 'en' ? 'ltr' : 'rtl'}>
                            <div 
                              dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewLanguage === 'ar' 
                                  ? previewData.preview_ar 
                                  : previewLanguage === 'en' 
                                    ? previewData.preview_en
                                    : `${previewData.preview_ar}<hr/>${previewData.preview_en}`) }} 
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-12 text-slate-500 border-2 border-dashed rounded-lg">
                          <Eye className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>{isRTL ? 'اضغط معاينة لعرض القالب' : 'Click Preview to see the template'}</p>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ) : (
            <Card className="h-[600px] flex items-center justify-center">
              <div className="text-center text-slate-500">
                <FileText className="h-16 w-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">
                  {isRTL ? 'اختر قالباً للتعديل' : 'Select a template to edit'}
                </p>
                <p className="text-sm mt-1">
                  {isRTL 
                    ? 'اختر من القائمة على اليسار' 
                    : 'Choose from the list on the left'}
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Create Template Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {isRTL ? 'إنشاء قالب جديد' : 'Create New Template'}
            </DialogTitle>
            <DialogDescription>
              {isRTL 
                ? 'اختر نوع الوثيقة وأدخل المحتوى' 
                : 'Select document type and enter content'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Document Type Selection */}
            <div>
              <Label>{isRTL ? 'نوع الوثيقة' : 'Document Type'} *</Label>
              <Select
                value={newTemplateForm.document_type_code}
                onValueChange={(val) => setNewTemplateForm(prev => ({
                  ...prev,
                  document_type_code: val
                }))}
              >
                <SelectTrigger data-testid="new-template-doc-type">
                  <SelectValue placeholder={isRTL ? 'اختر نوع الوثيقة' : 'Select document type'} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <div key={cat.code}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase">
                        {isRTL ? cat.name_ar : cat.name_en}
                      </div>
                      {documentTypesForDropdown
                        .filter(dt => dt.category === cat.code)
                        .map(dt => (
                          <SelectItem key={dt.code} value={dt.code}>
                            {isRTL ? dt.name_ar : dt.name_en}
                          </SelectItem>
                        ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Arabic Content */}
            <div>
              <Label>{isRTL ? 'المحتوى العربي' : 'Arabic Content'}</Label>
              <Textarea
                value={newTemplateForm.content_ar}
                onChange={(e) => setNewTemplateForm(prev => ({
                  ...prev,
                  content_ar: e.target.value
                }))}
                className="min-h-[100px]"
                dir="rtl"
                data-testid="new-template-content-ar"
              />
            </div>
            
            {/* English Content */}
            <div>
              <Label>{isRTL ? 'المحتوى الإنجليزي' : 'English Content'}</Label>
              <Textarea
                value={newTemplateForm.content_en}
                onChange={(e) => setNewTemplateForm(prev => ({
                  ...prev,
                  content_en: e.target.value
                }))}
                className="min-h-[100px]"
                data-testid="new-template-content-en"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleCreateTemplate} 
              disabled={saving || !newTemplateForm.document_type_code}
              data-testid="create-template-submit"
            >
              {saving 
                ? (isRTL ? 'جاري الإنشاء...' : 'Creating...') 
                : (isRTL ? 'إنشاء' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish Confirmation Dialog */}
      <Dialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              {isRTL ? 'نشر القالب' : 'Publish Template'}
            </DialogTitle>
            <DialogDescription>
              {isRTL 
                ? 'بعد النشر، لا يمكن تعديل هذا القالب. سيتم استخدامه لإصدار الوثائق.' 
                : 'Once published, this template cannot be edited. It will be used for document issuance.'}
            </DialogDescription>
          </DialogHeader>
          
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{isRTL ? 'تأكيد' : 'Confirmation'}</AlertTitle>
            <AlertDescription>
              {isRTL 
                ? 'سيتم أرشفة أي نسخة منشورة سابقة تلقائياً.' 
                : 'Any previously published version will be automatically archived.'}
            </AlertDescription>
          </Alert>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPublishDialog(false)}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              onClick={handlePublishTemplate} 
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-publish-btn"
            >
              {saving 
                ? (isRTL ? 'جاري النشر...' : 'Publishing...') 
                : (isRTL ? 'نشر' : 'Publish')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5 text-slate-500" />
              {isRTL ? 'أرشفة القالب' : 'Archive Template'}
            </DialogTitle>
            <DialogDescription>
              {isRTL 
                ? 'القوالب المؤرشفة لا يمكن استخدامها لإصدار وثائق جديدة.' 
                : 'Archived templates cannot be used for new document issuance.'}
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchiveDialog(false)}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleArchiveTemplate} 
              disabled={saving}
              variant="destructive"
              data-testid="confirm-archive-btn"
            >
              {saving 
                ? (isRTL ? 'جاري الأرشفة...' : 'Archiving...') 
                : (isRTL ? 'أرشفة' : 'Archive')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Placeholders Dialog */}
      <Dialog open={showPlaceholdersDialog} onOpenChange={setShowPlaceholdersDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {isRTL ? 'المتغيرات المتاحة' : 'Available Placeholders'}
            </DialogTitle>
            <DialogDescription>
              {isRTL 
                ? 'انسخ المتغير وألصقه في محتوى القالب' 
                : 'Copy a placeholder and paste it in your template content'}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="flex-1 mt-4">
            <div className="space-y-2 pr-4">
              {placeholders.map(p => (
                <div 
                  key={p.key}
                  className="flex items-center justify-between p-2 rounded-lg border hover:bg-slate-50"
                >
                  <div>
                    <code className="text-sm font-mono bg-slate-100 px-2 py-0.5 rounded">
                      {`{{${p.key}}}`}
                    </code>
                    <p className="text-xs text-slate-500 mt-1">
                      {isRTL ? p.label_ar : p.label_en}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => copyPlaceholder(p.key)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {isRTL ? 'معاينة القالب' : 'Template Preview'}
            </DialogTitle>
            <DialogDescription>
              {isRTL ? 'معاينة ببيانات تجريبية' : 'Preview with mock data'}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="flex-1 mt-4">
            {previewData && (
              <div className="bg-white border rounded-lg p-8 shadow-inner">
                <div 
                  className="prose max-w-none"
                  dir={previewLanguage === 'en' ? 'ltr' : 'rtl'}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewLanguage === 'ar' 
                      ? previewData.preview_ar 
                      : previewLanguage === 'en' 
                        ? previewData.preview_en
                        : `${previewData.preview_ar}<hr class="my-8"/>${previewData.preview_en}`) }} 
                />
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
